<template>
  <div class="user-edit-page py-3">
    <header class="user-toolbar">
      <div class="user-toolbar__title">
        <span class="user-toolbar__accent" aria-hidden="true"></span>
        <div>
          <h1>Edit User</h1>
          <p>Update profile, access, and credentials for user #{{ $route.params.id }}</p>
        </div>
      </div>
      <div class="user-toolbar__actions">
        <span v-if="lastFetchedAt && !isLoading" class="user-updated">
          Fetched {{ lastFetchedShort }}
        </span>
        <CButton size="sm" class="back-btn" @click="$router.push('/settings/users')">
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="back-btn__icon" aria-hidden="true">
            <line x1="19" y1="12" x2="5" y2="12"/>
            <polyline points="12 19 5 12 12 5"/>
          </svg>
          <span>Back to Users</span>
        </CButton>
      </div>
    </header>

    <CAlert v-if="errorMessage" color="danger" show class="user-alert">{{ errorMessage }}</CAlert>
    <CAlert v-if="successMessage" color="success" show class="user-alert">{{ successMessage }}</CAlert>

    <div v-if="isLoading" class="user-loading-card">
      <CSpinner size="sm" class="mr-2" />
      <span>Loading user details…</span>
    </div>

    <form v-else class="user-form" @submit.prevent="updateUser">
      <!-- Profile section -->
      <section class="user-section">
        <header class="user-section__head">
          <div class="user-section__heading">
            <span class="user-section__step">1</span>
            <div>
              <h2>Profile</h2>
              <p>Photo and contact details for this user</p>
            </div>
          </div>
        </header>

        <div class="user-section__body">
          <div class="profile-row">
            <div class="profile-photo">
              <div
                class="profile-photo__avatar"
                :style="!showPhotoImage ? { background: avatarGradient } : null"
              >
                <img
                  v-if="showPhotoImage"
                  :src="photoPreview"
                  alt="Profile preview"
                  @error="onPhotoLoadError"
                />
                <span v-else>{{ avatarInitials }}</span>
              </div>
              <input
                ref="photoInput"
                type="file"
                accept="image/*"
                class="profile-photo__input"
                @change="onPhotoSelected"
              />
              <div class="profile-photo__actions">
                <button type="button" class="ghost-btn" @click="triggerPhotoPicker">
                  {{ photoPreview ? 'Change Photo' : 'Upload Photo' }}
                </button>
                <button v-if="photoPreview" type="button" class="ghost-btn ghost-btn--danger" @click="clearPhoto">
                  Remove
                </button>
              </div>
              <p class="profile-photo__hint">PNG or JPG, up to 2 MB.</p>
            </div>

            <div class="profile-fields">
              <div class="profile-summary">
                <div class="profile-summary__name">{{ form.name || 'Unnamed user' }}</div>
                <div class="profile-summary__tags">
                  <span :class="['role-pill', roleClass(form.role)]">{{ formatRole(form.role) }}</span>
                  <span class="role-pill role-pill--gray">{{ form.organisation || '—' }}</span>
                  <span v-if="form.department && form.department !== 'None'" class="role-pill role-pill--gray">{{ form.department }}</span>
                </div>
              </div>

              <div class="form-grid">
                <div class="form-field">
                  <label class="form-label" for="user-name">Full name</label>
                  <input
                    id="user-name"
                    v-model.trim="form.name"
                    type="text"
                    class="form-input"
                    autocomplete="name"
                    placeholder="e.g. Anitha Raj"
                  />
                </div>

                <div class="form-field">
                  <label class="form-label" for="user-email">Email</label>
                  <input
                    id="user-email"
                    v-model.trim="form.email"
                    type="email"
                    class="form-input"
                    autocomplete="email"
                    placeholder="name@example.com"
                  />
                </div>

                <div class="form-field">
                  <label class="form-label" for="user-phone">Phone number</label>
                  <input
                    id="user-phone"
                    v-model.trim="form.phone"
                    type="tel"
                    class="form-input"
                    autocomplete="tel"
                    placeholder="+91 98xxxxxxxx"
                  />
                </div>

                <div class="form-field">
                  <label class="form-label" for="user-biometric">Biometric ID</label>
                  <input
                    id="user-biometric"
                    v-model.trim="form.biometric_id"
                    type="text"
                    class="form-input"
                    placeholder="Fingerprint PIN on the device, e.g. 15"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Personal details section -->
      <section class="user-section">
        <header class="user-section__head">
          <div class="user-section__heading">
            <span class="user-section__step">2</span>
            <div>
              <h2>Personal Details</h2>
              <p>Additional personal information</p>
            </div>
          </div>
          <span class="user-hint-pill">Optional</span>
        </header>

        <div class="user-section__body">
          <div class="form-grid form-grid--triple">
            <div class="form-field">
              <label class="form-label" for="user-alt-phone">Alternate phone</label>
              <input
                id="user-alt-phone"
                v-model.trim="form.alternate_phone"
                type="tel"
                class="form-input"
                placeholder="Optional"
              />
            </div>
            <div class="form-field">
              <label class="form-label">Date of birth</label>
              <SingleDatePicker v-model="form.date_of_birth" />
            </div>
            <div class="form-field">
              <label class="form-label">Gender</label>
              <AppDropdown v-model="form.gender" :options="genderOptions" placeholder="Select gender" />
            </div>
            <div class="form-field">
              <label class="form-label">Blood group</label>
              <AppDropdown v-model="form.blood_group" :options="bloodGroupOptions" placeholder="Select" />
            </div>
            <div class="form-field">
              <label class="form-label">Marital status</label>
              <AppDropdown v-model="form.marital_status" :options="maritalOptions" placeholder="Select" />
            </div>
          </div>
        </div>
      </section>

      <!-- Address section -->
      <section class="user-section">
        <header class="user-section__head">
          <div class="user-section__heading">
            <span class="user-section__step">3</span>
            <div>
              <h2>Address</h2>
              <p>Where this user is based</p>
            </div>
          </div>
          <span class="user-hint-pill">Optional</span>
        </header>

        <div class="user-section__body">
          <div class="form-grid form-grid--single">
            <div class="form-field">
              <label class="form-label" for="user-address">Street address</label>
              <textarea
                id="user-address"
                v-model="form.address"
                rows="3"
                class="form-input form-textarea"
                placeholder="House / building, street, locality"
              ></textarea>
            </div>

            <div class="form-grid form-grid--triple">
              <div class="form-field">
                <label class="form-label" for="user-city">City</label>
                <input
                  id="user-city"
                  v-model.trim="form.city"
                  type="text"
                  class="form-input"
                  autocomplete="address-level2"
                  placeholder="City"
                />
              </div>
              <div class="form-field">
                <label class="form-label" for="user-state">State</label>
                <input
                  id="user-state"
                  v-model.trim="form.state"
                  type="text"
                  class="form-input"
                  autocomplete="address-level1"
                  placeholder="State"
                />
              </div>
              <div class="form-field">
                <label class="form-label" for="user-pincode">Pincode</label>
                <input
                  id="user-pincode"
                  v-model.trim="form.pincode"
                  type="text"
                  inputmode="numeric"
                  pattern="[0-9]*"
                  maxlength="10"
                  class="form-input"
                  autocomplete="postal-code"
                  placeholder="6-digit"
                />
              </div>
              <div class="form-field">
                <label class="form-label" for="user-country">Country</label>
                <input
                  id="user-country"
                  v-model.trim="form.country"
                  type="text"
                  class="form-input"
                  autocomplete="country-name"
                  placeholder="Country"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Emergency contact section -->
      <section class="user-section">
        <header class="user-section__head">
          <div class="user-section__heading">
            <span class="user-section__step">4</span>
            <div>
              <h2>Emergency Contact</h2>
              <p>Whom to reach in case of an emergency</p>
            </div>
          </div>
          <span class="user-hint-pill">Optional</span>
        </header>

        <div class="user-section__body">
          <div class="form-grid form-grid--triple">
            <div class="form-field">
              <label class="form-label" for="user-ec-name">Contact name</label>
              <input id="user-ec-name" v-model.trim="form.emergency_contact_name" type="text" class="form-input" placeholder="Full name" />
            </div>
            <div class="form-field">
              <label class="form-label" for="user-ec-phone">Contact phone</label>
              <input id="user-ec-phone" v-model.trim="form.emergency_contact_phone" type="tel" class="form-input" placeholder="Phone number" />
            </div>
            <div class="form-field">
              <label class="form-label">Relation</label>
              <AppDropdown v-model="form.emergency_contact_relation" :options="relationOptions" placeholder="Select relation" searchable />
            </div>
          </div>
        </div>
      </section>

      <!-- Access section -->
      <section class="user-section">
        <header class="user-section__head">
          <div class="user-section__heading">
            <span class="user-section__step">5</span>
            <div>
              <h2>Access</h2>
              <p>Where they belong and what they can do</p>
            </div>
          </div>
        </header>

        <div class="user-section__body">
          <div class="form-grid">
            <div class="form-field">
              <label class="form-label">Organisation</label>
              <AppDropdown
                v-model="form.organisationId"
                :options="organisationDropdownOptions"
                searchable
                search-placeholder="Search organisation…"
                :placeholder="isLoadingOrganisations ? 'Loading organisations…' : 'Select organisation'"
              />
            </div>
            <div class="form-field">
              <label class="form-label">Department</label>
              <AppDropdown
                v-model="form.departmentId"
                :options="departmentDropdownOptions"
                searchable
                search-placeholder="Search department…"
                :placeholder="departmentPlaceholder"
              />
            </div>
            <div class="form-field">
              <label class="form-label">State</label>
              <AppDropdown
                v-model="form.stateId"
                :options="stateDropdownOptions"
                searchable
                search-placeholder="Search state…"
                :placeholder="isLoadingStates ? 'Loading states…' : 'Select state'"
              />
            </div>
            <div class="form-field">
              <label class="form-label">Branch</label>
              <AppDropdown
                v-model="form.branchId"
                :options="branchDropdownOptions"
                searchable
                search-placeholder="Search branch…"
                :placeholder="branchPlaceholder"
              />
            </div>
            <div class="form-field form-field--span2">
              <label class="form-label">Role</label>
              <AppDropdown
                v-model="form.role"
                :options="roleDropdownOptions"
                searchable
                search-placeholder="Search role…"
                placeholder="Select a role"
              />
            </div>
          </div>
        </div>
      </section>

      <!-- Employment section -->
      <section class="user-section">
        <header class="user-section__head">
          <div class="user-section__heading">
            <span class="user-section__step">6</span>
            <div>
              <h2>Employment</h2>
              <p>HR details for the staff record</p>
            </div>
          </div>
          <span class="user-hint-pill">Optional</span>
        </header>

        <div class="user-section__body">
          <div class="form-grid form-grid--triple">
            <div class="form-field">
              <label class="form-label" for="user-emp-code">Employee code</label>
              <input id="user-emp-code" v-model.trim="form.employee_code" type="text" class="form-input" placeholder="e.g. MD-0042" />
            </div>
            <div class="form-field">
              <label class="form-label" for="user-designation">Designation</label>
              <input id="user-designation" v-model.trim="form.designation" type="text" class="form-input" placeholder="e.g. Telesales Executive" />
            </div>
            <div class="form-field">
              <label class="form-label" for="user-doj">Date of joining</label>
              <input id="user-doj" v-model="form.date_of_joining" type="date" class="form-input" />
            </div>
            <div class="form-field">
              <label class="form-label">Employment type</label>
              <AppDropdown v-model="form.employment_type" :options="employmentTypeOptions" placeholder="Select type" />
            </div>
          </div>
        </div>
      </section>

      <!-- KYC & Bank section -->
      <section class="user-section">
        <header class="user-section__head">
          <div class="user-section__heading">
            <span class="user-section__step">7</span>
            <div>
              <h2>KYC &amp; Bank</h2>
              <p>Identity proof and payroll account details</p>
            </div>
          </div>
          <span class="user-hint-pill">Optional · Sensitive</span>
        </header>

        <div class="user-section__body">
          <div class="form-grid">
            <div class="form-field">
              <label class="form-label" for="user-pan">PAN number</label>
              <input id="user-pan" v-model.trim="form.pan_number" type="text" class="form-input" maxlength="10" style="text-transform: uppercase" placeholder="ABCDE1234F" />
            </div>
            <div class="form-field">
              <label class="form-label" for="user-aadhaar">Aadhaar number</label>
              <input id="user-aadhaar" v-model.trim="form.aadhaar_number" type="text" inputmode="numeric" maxlength="14" class="form-input" placeholder="12-digit" />
            </div>
            <div class="form-field">
              <label class="form-label" for="user-bank-acc">Bank account number</label>
              <input id="user-bank-acc" v-model.trim="form.bank_account_number" type="text" class="form-input" placeholder="Account number" />
            </div>
            <div class="form-field">
              <label class="form-label" for="user-bank-name">Bank name</label>
              <input id="user-bank-name" v-model.trim="form.bank_name" type="text" class="form-input" placeholder="e.g. State Bank of India" />
            </div>
            <div class="form-field">
              <label class="form-label" for="user-bank-ifsc">IFSC</label>
              <input id="user-bank-ifsc" v-model.trim="form.bank_ifsc" type="text" class="form-input" maxlength="11" style="text-transform: uppercase" placeholder="SBIN0001234" />
            </div>
          </div>
        </div>
      </section>

      <!-- CV / Document section -->
      <section class="user-section">
        <header class="user-section__head">
          <div class="user-section__heading">
            <span class="user-section__step">8</span>
            <div>
              <h2>CV / Document</h2>
              <p>Upload the staff member's CV — PDF or image</p>
            </div>
          </div>
          <span class="user-hint-pill">Optional</span>
        </header>

        <div class="user-section__body">
          <div class="cv-upload">
            <input ref="cvInput" type="file" accept=".pdf,image/*" class="cv-upload__input" @change="onCvSelected" />
            <div class="cv-upload__row">
              <div class="cv-upload__info">
                <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
                <span v-if="cvName" class="cv-upload__name">{{ cvName }}</span>
                <a v-else-if="showExistingCv" :href="cvExisting" target="_blank" rel="noopener" class="cv-upload__link">View current CV</a>
                <span v-else class="cv-upload__empty">No file uploaded</span>
              </div>
              <div class="cv-upload__actions">
                <button type="button" class="ghost-btn" @click="triggerCvPicker">
                  {{ (cvName || showExistingCv) ? 'Change File' : 'Upload File' }}
                </button>
                <button v-if="cvName || showExistingCv" type="button" class="ghost-btn ghost-btn--danger" @click="clearCv">
                  Remove
                </button>
              </div>
            </div>
            <p class="cv-upload__hint">PDF, JPG, PNG or WEBP · up to 5 MB.</p>
          </div>
        </div>
      </section>

      <!-- Admin notes section -->
      <section class="user-section">
        <header class="user-section__head">
          <div class="user-section__heading">
            <span class="user-section__step">9</span>
            <div>
              <h2>Admin Notes</h2>
              <p>Internal notes visible to administrators</p>
            </div>
          </div>
          <span class="user-hint-pill">Optional</span>
        </header>

        <div class="user-section__body">
          <div class="form-grid form-grid--single">
            <div class="form-field">
              <textarea v-model="form.notes" rows="3" class="form-input form-textarea" placeholder="Any additional notes about this staff member"></textarea>
            </div>
          </div>
        </div>
      </section>

      <!-- Account status section -->
      <section class="user-section">
        <header class="user-section__head">
          <div class="user-section__heading">
            <span class="user-section__step">10</span>
            <div>
              <h2>Account Status</h2>
              <p>Inactive users keep all their data but cannot log in</p>
            </div>
          </div>
          <span :class="['status-pill', isInactive ? 'badge--red' : 'badge--green']">
            {{ isInactive ? 'Inactive' : 'Active' }}
          </span>
        </header>

        <div class="user-section__body">
          <div class="account-status">
            <button
              type="button"
              :class="['status-toggle', { 'status-toggle--inactive': isInactive }]"
              :aria-pressed="!isInactive"
              @click="toggleEmploymentStatus"
            >
              <span class="status-toggle__track">
                <span class="status-toggle__thumb"></span>
              </span>
              <span class="status-toggle__label">
                {{ isInactive ? 'Inactive — login disabled' : 'Active — login allowed' }}
              </span>
            </button>
            <p v-if="isInactive" class="account-status__warning">
              This user will not be able to sign in. Their records, orders, and history remain intact.
            </p>
          </div>
        </div>
      </section>

      <!-- Credentials section -->
      <section class="user-section">
        <header class="user-section__head">
          <div class="user-section__heading">
            <span class="user-section__step">11</span>
            <div>
              <h2>Change Password</h2>
              <p>Leave blank to keep the current password — fill in to reset</p>
            </div>
          </div>
          <span class="user-hint-pill">Optional</span>
        </header>

        <div class="user-section__body">
          <div class="form-grid">
            <div class="form-field">
              <label class="form-label" for="user-password">New password</label>
              <div class="password-wrap">
                <input
                  id="user-password"
                  v-model="passwordForm.password"
                  :type="showPassword ? 'text' : 'password'"
                  class="form-input"
                  autocomplete="new-password"
                  placeholder="At least 8 characters"
                />
                <button
                  type="button"
                  class="password-toggle"
                  :aria-label="showPassword ? 'Hide password' : 'Show password'"
                  @click="showPassword = !showPassword"
                >
                  {{ showPassword ? 'Hide' : 'Show' }}
                </button>
              </div>
              <div v-if="passwordForm.password" class="password-strength" :class="`password-strength--${passwordStrength.key}`">
                <span class="password-strength__bar"><span :style="{ width: passwordStrength.percent + '%' }"></span></span>
                <span class="password-strength__label">{{ passwordStrength.label }}</span>
              </div>
            </div>

            <div class="form-field">
              <label class="form-label" for="user-password-confirm">Confirm new password</label>
              <input
                id="user-password-confirm"
                v-model="passwordForm.passwordConfirmation"
                :type="showPassword ? 'text' : 'password'"
                class="form-input"
                :class="{ 'form-input--error': passwordMismatch }"
                autocomplete="new-password"
                placeholder="Re-enter password"
              />
              <span v-if="passwordMismatch" class="form-error">Passwords do not match</span>
            </div>
          </div>
        </div>
      </section>

      <footer class="user-form__footer">
        <span class="form-meta">Last fetched: {{ lastFetchedLabel }}</span>
        <div class="user-form__actions">
          <CButton
            type="button"
            size="sm"
            class="back-btn"
            :disabled="isSaving"
            @click="loadUser"
          >
            Discard changes
          </CButton>
          <CButton
            type="submit"
            size="sm"
            class="submit-btn"
            :disabled="isSaving || passwordMismatch"
          >
            <svg v-if="!isSaving" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" class="submit-btn__icon" aria-hidden="true">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            <span>{{ isSaving ? 'Updating…' : 'Update User' }}</span>
          </CButton>
        </div>
      </footer>
    </form>

    <AppConfirmDialog
      :visible="showLeaveDialog"
      title="Unsaved changes"
      message="You have unsaved changes on this page. Save them before leaving, discard them, or stay to keep editing."
      confirm-label="Save & continue"
      busy-label="Saving…"
      discard-label="Discard"
      cancel-label="Stay"
      variant="warning"
      :busy="leaveDialogBusy"
      @confirm="onLeaveSave"
      @discard="onLeaveDiscard"
      @cancel="onLeaveCancel"
    />
  </div>
</template>

<script>
import axios from 'axios'
import AppDropdown from '@/components/AppDropdown.vue'
import AppConfirmDialog from '@/components/AppConfirmDialog.vue'
import SingleDatePicker from '@/components/SingleDatePicker.vue'
import statesApi from '@/api/states.js'
import branchesApi from '@/api/branches.js'
import {
  buildPasswordPayload,
  buildUserDetailForm,
  buildUserUpdatePayload,
  getCachedUserById,
  normalizeUserDetailResponse,
  normalizeUsersResponse,
  setUserCache,
  upsertCachedUser,
  ROLE_OPTIONS
} from './userHelpers'

const NO_DEPARTMENT = null

const PHOTO_MAX_BYTES = 2 * 1024 * 1024
const CV_MAX_BYTES = 5 * 1024 * 1024

const GENDER_OPTIONS = ['Male', 'Female', 'Other', 'Prefer not to say']
const MARITAL_OPTIONS = ['Single', 'Married', 'Divorced', 'Widowed', 'Separated']
const BLOOD_GROUP_OPTIONS = ['A+', 'A-', 'B+', 'B-', 'O+', 'O-', 'AB+', 'AB-']
const EMPLOYMENT_TYPE_OPTIONS = ['full_time', 'part_time', 'contract', 'intern', 'consultant']
const RELATION_OPTIONS = ['Father', 'Mother', 'Spouse', 'Sibling', 'Child', 'Guardian', 'Friend', 'Other']

function firstDefined (...values) {
  for (const value of values) {
    if (value !== null && value !== undefined && value !== '') return value
  }
  return ''
}

function emptyExtras () {
  return {
    phone: '',
    alternate_phone: '',
    address: '',
    city: '',
    state: '',
    pincode: '',
    country: '',
    date_of_birth: '',
    gender: '',
    blood_group: '',
    marital_status: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relation: '',
    employee_code: '',
    biometric_id: '',
    designation: '',
    date_of_joining: '',
    employment_type: '',
    pan_number: '',
    aadhaar_number: '',
    bank_account_number: '',
    bank_name: '',
    bank_ifsc: '',
    notes: ''
  }
}

export default {
  name: 'User',
  components: { AppDropdown, AppConfirmDialog, SingleDatePicker },
  data () {
    return {
      isLoadingUser: false,
      isLoadingOrganisations: false,
      isLoadingDepartments: false,
      isLoadingStates: false,
      isLoadingBranches: false,
      suppressDepartmentReset: false,
      // Mirror of suppressDepartmentReset for the new branch/state cascade —
      // keeps the user's saved branchId alive until the branch list loads.
      suppressBranchReset: false,
      isSaving: false,
      showPassword: false,
      errorMessage: '',
      successMessage: '',
      lastFetchedAt: null,
      photoPreview: '',
      photoFile: null,
      photoExisting: '',
      photoRemoved: false,
      photoLoadFailed: false,
      cvFile: null,
      cvName: '',
      cvExisting: '',
      cvRemoved: false,
      formBaseline: null,
      showLeaveDialog: false,
      leaveDialogBusy: false,
      pendingNext: null,
      organisations: [],
      departments: [],
      states: [],
      branches: [],
      form: {
        name: '',
        email: '',
        role: 'None',
        organisation: '',
        department: '',
        organisationId: null,
        departmentId: NO_DEPARTMENT,
        stateId: null,
        branchId: null,
        employmentStatus: 'active',
        ...emptyExtras()
      },
      genderOptions: GENDER_OPTIONS.map((v) => ({ value: v, label: v })),
      maritalOptions: MARITAL_OPTIONS.map((v) => ({ value: v, label: v })),
      bloodGroupOptions: BLOOD_GROUP_OPTIONS.map((v) => ({ value: v, label: v })),
      employmentTypeOptions: EMPLOYMENT_TYPE_OPTIONS.map((v) => ({ value: v, label: v.replace(/_/g, ' ') })),
      relationOptions: RELATION_OPTIONS.map((v) => ({ value: v, label: v })),
      passwordForm: {
        password: '',
        passwordConfirmation: ''
      }
    }
  },
  computed: {
    isLoading () {
      // Gate the form behind both fetches so AppDropdown mounts with options
      // already populated — otherwise the saved organisation/department id
      // can't resolve to a label.
      return this.isLoadingUser || this.isLoadingOrganisations
    },
    organisationDropdownOptions () {
      return this.organisations.map((org) => ({ value: org.id, label: org.name }))
    },
    departmentDropdownOptions () {
      return [
        { value: NO_DEPARTMENT, label: 'No department' },
        ...this.departments.map((dept) => ({ value: dept.id, label: dept.name }))
      ]
    },
    departmentPlaceholder () {
      if (!this.form.organisationId) return 'Pick an organisation first'
      if (this.isLoadingDepartments) return 'Loading departments…'
      return 'Select department'
    },
    stateDropdownOptions () {
      return [
        { value: null, label: 'No state' },
        ...this.states.map((s) => ({ value: s.id, label: s.name }))
      ]
    },
    branchDropdownOptions () {
      // Cascade off State (both are global). With no state picked, all
      // branches show — picking a branch then auto-fills its state.
      const filtered = this.form.stateId
        ? this.branches.filter((b) => Number(b.state_id) === Number(this.form.stateId))
        : this.branches
      return [
        { value: null, label: 'No branch' },
        ...filtered.map((b) => ({ value: b.id, label: b.name }))
      ]
    },
    branchPlaceholder () {
      if (this.isLoadingBranches) return 'Loading branches…'
      if (!this.branches.length) return 'No branches in the system'
      if (this.form.stateId && !this.branchDropdownOptions.find((o) => o.value !== null)) return 'No branches under this state'
      return 'Select branch'
    },
    roleDropdownOptions () {
      return ROLE_OPTIONS.map((role) => ({
        value: role,
        label: role === 'None' ? 'No role' : this.formatRole(role)
      }))
    },
    avatarInitials () {
      const source = this.form.name || this.form.email || 'U'
      const initials = source
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word[0].toUpperCase())
        .join('')
      return initials || 'U'
    },
    avatarGradient () {
      const palette = [
        'linear-gradient(135deg, #0e5b46 0%, #16a07a 100%)',
        'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
        'linear-gradient(135deg, #6d28d9 0%, #a855f7 100%)',
        'linear-gradient(135deg, #b45309 0%, #f59e0b 100%)',
        'linear-gradient(135deg, #be185d 0%, #ec4899 100%)',
        'linear-gradient(135deg, #115e59 0%, #14b8a6 100%)'
      ]
      const seed = String(this.form.name || this.form.email || this.$route.params.id || '?')
      let hash = 0
      for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
      return palette[hash % palette.length]
    },
    lastFetchedLabel () {
      return this.lastFetchedAt ? this.lastFetchedAt.toLocaleString() : 'Not fetched yet'
    },
    lastFetchedShort () {
      if (!this.lastFetchedAt) return ''
      const hours = String(this.lastFetchedAt.getHours()).padStart(2, '0')
      const minutes = String(this.lastFetchedAt.getMinutes()).padStart(2, '0')
      return `${hours}:${minutes}`
    },
    passwordMismatch () {
      if (!this.passwordForm.password || !this.passwordForm.passwordConfirmation) return false
      return this.passwordForm.password !== this.passwordForm.passwordConfirmation
    },
    passwordStrength () {
      const value = this.passwordForm.password || ''
      if (!value) return { key: 'empty', label: '', percent: 0 }
      let score = 0
      if (value.length >= 8) score++
      if (/[A-Z]/.test(value) && /[a-z]/.test(value)) score++
      if (/\d/.test(value)) score++
      if (/[^A-Za-z0-9]/.test(value)) score++
      if (value.length >= 12) score++
      const map = [
        { key: 'weak', label: 'Weak', percent: 25 },
        { key: 'weak', label: 'Weak', percent: 35 },
        { key: 'fair', label: 'Fair', percent: 55 },
        { key: 'good', label: 'Good', percent: 75 },
        { key: 'strong', label: 'Strong', percent: 90 },
        { key: 'strong', label: 'Strong', percent: 100 }
      ]
      return map[Math.min(score, map.length - 1)]
    },
    formSnapshot () {
      // Use ids for organisation/department so the dirty-check is stable even
      // while the names are still being resolved from the freshly-loaded lists.
      return JSON.stringify({
        name: this.form.name,
        email: this.form.email,
        role: this.form.role,
        organisationId: this.form.organisationId,
        departmentId: this.form.departmentId,
        stateId: this.form.stateId,
        branchId: this.form.branchId,
        phone: this.form.phone,
        alternate_phone: this.form.alternate_phone,
        address: this.form.address,
        city: this.form.city,
        state: this.form.state,
        pincode: this.form.pincode,
        country: this.form.country,
        date_of_birth: this.form.date_of_birth,
        gender: this.form.gender,
        blood_group: this.form.blood_group,
        marital_status: this.form.marital_status,
        emergency_contact_name: this.form.emergency_contact_name,
        emergency_contact_phone: this.form.emergency_contact_phone,
        emergency_contact_relation: this.form.emergency_contact_relation,
        employee_code: this.form.employee_code,
        biometric_id: this.form.biometric_id,
        designation: this.form.designation,
        date_of_joining: this.form.date_of_joining,
        employment_type: this.form.employment_type,
        pan_number: this.form.pan_number,
        aadhaar_number: this.form.aadhaar_number,
        bank_account_number: this.form.bank_account_number,
        bank_name: this.form.bank_name,
        bank_ifsc: this.form.bank_ifsc,
        notes: this.form.notes,
        employmentStatus: this.form.employmentStatus,
        photoFileSig: this.photoFile ? `${this.photoFile.name}-${this.photoFile.size}-${this.photoFile.lastModified}` : '',
        photoRemoved: this.photoRemoved,
        cvFileSig: this.cvFile ? `${this.cvFile.name}-${this.cvFile.size}-${this.cvFile.lastModified}` : '',
        cvRemoved: this.cvRemoved,
        password: this.passwordForm.password,
        passwordConfirmation: this.passwordForm.passwordConfirmation
      })
    },
    isDirty () {
      if (this.formBaseline === null) return false
      return this.formBaseline !== this.formSnapshot
    },
    showPhotoImage () {
      return !!this.photoPreview && !this.photoLoadFailed
    },
    showExistingCv () {
      return !!this.cvExisting && !this.cvRemoved
    },
    isInactive () {
      return String(this.form.employmentStatus || '').toLowerCase() === 'inactive'
    }
  },
  watch: {
    photoPreview () {
      this.photoLoadFailed = false
    },
    'form.organisationId' (newId, oldId) {
      if (newId === oldId) return
      if (this.organisations.length) {
        const org = this.organisations.find((o) => o.id === newId)
        this.form.organisation = org ? org.name : ''
      }
      if (!this.suppressDepartmentReset) {
        this.form.departmentId = NO_DEPARTMENT
      }
      this.departments = []
      if (newId) this.loadDepartments(newId)
      // State + Branch are GLOBAL — independent of org. Org switching does
      // NOT touch them.
    },
    'form.departmentId' (newId) {
      if (!this.departments.length) return
      const dept = this.departments.find((d) => d.id === newId)
      this.form.department = dept ? dept.name : ''
    },
    'form.stateId' (newId, oldId) {
      if (newId === oldId || this.suppressBranchReset) return
      // Clear branch if it no longer belongs to the new state.
      if (!this.form.branchId) return
      const branch = this.branches.find((b) => Number(b.id) === Number(this.form.branchId))
      if (branch && Number(branch.state_id) !== Number(newId)) {
        this.form.branchId = null
      }
    },
    'form.branchId' (newId) {
      // Auto-fill stateId from the selected branch (branch carries state).
      if (!newId || !this.branches.length) return
      const branch = this.branches.find((b) => Number(b.id) === Number(newId))
      if (branch && Number(branch.state_id) !== Number(this.form.stateId)) {
        this.suppressBranchReset = true
        this.form.stateId = Number(branch.state_id)
        this.$nextTick(() => { this.suppressBranchReset = false })
      }
    }
  },
  created () {
    this.loadOrganisations()
    this.loadStates()
    this.loadBranches()
    this.loadUser()
  },
  mounted () {
    window.addEventListener('beforeunload', this.onBeforeUnload)
  },
  beforeDestroy () {
    window.removeEventListener('beforeunload', this.onBeforeUnload)
  },
  beforeRouteLeave (_to, _from, next) {
    if (!this.isDirty || this.isSaving) {
      next()
      return
    }
    this.pendingNext = next
    this.showLeaveDialog = true
  },
  methods: {
    formatRole (role) {
      if (!role || role === 'None') return 'No role'
      return String(role)
        .replace(/_/g, ' ')
        .split(' ')
        .map((word) => word ? word[0].toUpperCase() + word.slice(1).toLowerCase() : '')
        .join(' ')
    },
    roleClass (role) {
      const r = String(role || '').toLowerCase()
      if (!r || r === 'none') return 'role-pill--gray'
      if (r === 'superadmin' || r === 'admin') return 'role-pill--violet'
      if (r === 'telesales' || r === 'crm' || r === 'agent') return 'role-pill--blue'
      if (r === 'packer' || r === 'storekeeper' || r === 'field_staff') return 'role-pill--amber'
      if (r === 'accountant' || r === 'junior_accountant') return 'role-pill--teal'
      if (r === 'moderator' || r === 'sales_supervisor' || r === 'sales_admin' || r === 'ecom_manager' || r === 'marketing') return 'role-pill--green'
      return 'role-pill--gray'
    },
    goBack () {
      if (this.$router && this.$router.options) {
        this.$router.push({ path: '/users' })
        return
      }
      window.history.back()
    },
    triggerPhotoPicker () {
      this.$refs.photoInput && this.$refs.photoInput.click()
    },
    onPhotoSelected (event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file) return
      if (!/^image\//.test(file.type)) {
        this.errorMessage = 'Profile photo must be an image file.'
        return
      }
      if (file.size > PHOTO_MAX_BYTES) {
        this.errorMessage = 'Profile photo must be 2 MB or smaller.'
        return
      }
      this.errorMessage = ''
      this.photoFile = file
      this.photoRemoved = false
      const reader = new FileReader()
      reader.onload = (e) => {
        this.photoPreview = e.target.result
      }
      reader.readAsDataURL(file)
    },
    clearPhoto () {
      this.photoFile = null
      this.photoPreview = ''
      this.photoRemoved = !!this.photoExisting
    },
    onPhotoLoadError () {
      this.photoLoadFailed = true
    },
    triggerCvPicker () {
      this.$refs.cvInput && this.$refs.cvInput.click()
    },
    onCvSelected (event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''
      if (!file) return
      const isAllowed = file.type === 'application/pdf' || /^image\//.test(file.type)
      if (!isAllowed) {
        this.errorMessage = 'CV must be a PDF or an image file.'
        return
      }
      if (file.size > CV_MAX_BYTES) {
        this.errorMessage = 'CV must be 5 MB or smaller.'
        return
      }
      this.errorMessage = ''
      this.cvFile = file
      this.cvName = file.name
      this.cvRemoved = false
    },
    clearCv () {
      this.cvFile = null
      this.cvName = ''
      // Only flag a server-side removal if there was a saved CV to remove.
      this.cvRemoved = !!this.cvExisting
    },
    applyUser (user) {
      const normalizedUser = normalizeUserDetailResponse(user)
      const detail = buildUserDetailForm(normalizedUser)
      // Suppress the watcher-driven dept reset so the user's saved department
      // id survives until departments load and the watcher resolves its name.
      this.suppressDepartmentReset = true
      this.suppressBranchReset = true
      const employmentStatus = String(firstDefined(normalizedUser.employment_status, 'active')).toLowerCase() === 'inactive'
        ? 'inactive'
        : 'active'
      this.form = {
        ...detail,
        departmentId: detail.departmentId == null ? NO_DEPARTMENT : detail.departmentId,
        // detail.branchId comes from userHelpers buildUserDetailForm; stateId
        // is derived later in loadBranches() once the branch list lands.
        branchId: detail.branchId ?? null,
        stateId: detail.stateId ?? null,
        phone: firstDefined(normalizedUser.phone, normalizedUser.phone_number, normalizedUser.mobile),
        address: firstDefined(normalizedUser.address, normalizedUser.address_line, normalizedUser.street),
        city: firstDefined(normalizedUser.city),
        state: firstDefined(normalizedUser.state, normalizedUser.state_name),
        pincode: firstDefined(normalizedUser.pincode, normalizedUser.postal_code, normalizedUser.zip),
        employmentStatus
      }
      this.$nextTick(() => {
        this.suppressDepartmentReset = false
        this.suppressBranchReset = false
      })
      if (detail.organisationId) {
        this.loadDepartments(detail.organisationId)
      }
      const photoUrl = firstDefined(
        normalizedUser.profile_photo_url,
        normalizedUser.profile_photo,
        normalizedUser.avatar_url,
        normalizedUser.avatar,
        normalizedUser.photo_url,
        normalizedUser.photo
      )
      this.photoExisting = photoUrl ? String(photoUrl) : ''
      this.photoPreview = this.photoExisting
      this.photoFile = null
      this.photoRemoved = false
      const cvUrl = firstDefined(normalizedUser.cv_url, normalizedUser.cv, normalizedUser.resume_url)
      this.cvExisting = cvUrl ? String(cvUrl) : ''
      this.cvFile = null
      this.cvName = ''
      this.cvRemoved = false
      this.lastFetchedAt = new Date()
      this.$nextTick(() => {
        this.formBaseline = this.formSnapshot
      })
    },
    loadUser () {
      this.isLoadingUser = true
      this.errorMessage = ''
      this.successMessage = ''
      this.passwordForm.password = ''
      this.passwordForm.passwordConfirmation = ''

      const loadFromListFallback = () => {
        const cachedUser = getCachedUserById(this.$route.params.id)

        if (cachedUser) {
          this.applyUser(cachedUser)
          return Promise.resolve()
        }

        return axios.get(`${this.$apiAdress}/api/users?token=${localStorage.getItem('api_token')}`)
          .then((response) => {
            const normalized = normalizeUsersResponse(response.data)
            setUserCache(normalized.users)
            const user = normalized.users.find((item) => String(item.id) === String(this.$route.params.id))

            if (user) {
              this.applyUser(user)
              return
            }

            this.errorMessage = 'Unable to locate the requested user.'
          })
      }

      axios.get(`${this.$apiAdress}/api/users/${this.$route.params.id}?token=${localStorage.getItem('api_token')}`)
        .then((response) => {
          const user = normalizeUserDetailResponse(response.data)

          if (Object.keys(user).length) {
            this.applyUser(user)
            return
          }

          return loadFromListFallback()
        })
        .catch((error) => {
          console.log(error)
          return loadFromListFallback().catch((fallbackError) => {
            console.log(fallbackError)
            this.errorMessage = error?.response?.data?.message || 'Unable to load the user right now.'
          })
        })
        .finally(() => {
          this.isLoadingUser = false
        })
    },
    loadOrganisations () {
      this.isLoadingOrganisations = true
      const token = localStorage.getItem('api_token')
      axios.get(`${this.$apiAdress}/api/organisations?token=${token}`)
        .then((response) => {
          const data = response.data
          if (Array.isArray(data)) this.organisations = data
          else if (Array.isArray(data?.data)) this.organisations = data.data
          else this.organisations = []
          if (this.form.organisationId) {
            const org = this.organisations.find((o) => o.id === this.form.organisationId)
            if (org) this.form.organisation = org.name
          }
        })
        .catch((error) => { console.log(error) })
        .finally(() => { this.isLoadingOrganisations = false })
    },
    loadDepartments (organisationId) {
      this.isLoadingDepartments = true
      const token = localStorage.getItem('api_token')
      axios.get(`${this.$apiAdress}/api/department?token=${token}&organisation_id=${organisationId}`)
        .then((response) => {
          const data = response.data
          if (Array.isArray(data)) this.departments = data
          else if (Array.isArray(data?.data)) this.departments = data.data
          else this.departments = []
          const dept = this.departments.find((d) => d.id === this.form.departmentId)
          this.form.department = dept ? dept.name : ''
        })
        .catch((error) => { console.log(error) })
        .finally(() => { this.isLoadingDepartments = false })
    },
    loadStates () {
      this.isLoadingStates = true
      statesApi.list(this.$apiAdress)
        .then((data) => {
          this.states = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : [])
        })
        .catch((error) => { console.log(error) })
        .finally(() => { this.isLoadingStates = false })
    },
    loadBranches () {
      // Branches are GLOBAL — no org filter.
      this.isLoadingBranches = true
      branchesApi.list(this.$apiAdress)
        .then((data) => {
          this.branches = Array.isArray(data) ? data : (Array.isArray(data?.data) ? data.data : [])
          // Derive stateId from the saved branchId once the list arrives.
          // Matches the same pattern in EditUser.vue.
          if (this.form.branchId && !this.form.stateId) {
            const branch = this.branches.find((b) => Number(b.id) === Number(this.form.branchId))
            if (branch) {
              this.suppressBranchReset = true
              this.form.stateId = Number(branch.state_id)
              this.$nextTick(() => { this.suppressBranchReset = false })
            }
          }
          // Refresh the formBaseline so the auto-filled stateId doesn't
          // register as a dirty change.
          this.$nextTick(() => {
            if (this.formBaseline !== null) {
              this.formBaseline = this.formSnapshot
            }
          })
        })
        .catch((error) => { console.log(error) })
        .finally(() => { this.isLoadingBranches = false })
    },
    buildExtendedPayload () {
      const base = buildUserUpdatePayload(this.form)
      const extended = {
        ...base,
        phone: this.form.phone,
        address: this.form.address,
        city: this.form.city,
        state: this.form.state,
        pincode: this.form.pincode,
        employment_status: this.isInactive ? 'inactive' : 'active'
      }
      if (this.photoRemoved) {
        extended.remove_profile_photo = 1
      }
      if (this.cvRemoved) {
        extended.remove_cv = 1
      }
      return extended
    },
    toggleEmploymentStatus () {
      this.form.employmentStatus = this.isInactive ? 'active' : 'inactive'
    },
    updateUser () {
      this.isSaving = true
      this.errorMessage = ''
      this.successMessage = ''

      const payload = this.buildExtendedPayload()
      const url = `${this.$apiAdress}/api/users/${this.$route.params.id}?token=${localStorage.getItem('api_token')}`

      let request
      if (this.photoFile || this.cvFile) {
        const fd = new FormData()
        Object.entries(payload).forEach(([key, value]) => {
          if (value === undefined || value === null) return
          fd.append(key, value)
        })
        if (this.photoFile) fd.append('profile_photo', this.photoFile)
        if (this.cvFile) fd.append('cv_file', this.cvFile)
        request = axios.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
      } else {
        request = axios.post(url, payload)
      }

      return request
        .then(() => this.afterUpdate(payload))
        .catch((error) => {
          console.log(error)
          if (!error?.response || error?.response?.status === 404) {
            return this.afterUpdate(payload)
          }
          this.errorMessage = error?.response?.data?.message || 'Unable to update the user right now.'
        })
        .finally(() => {
          this.isSaving = false
        })
    },
    afterUpdate (payload) {
      upsertCachedUser(this.$route.params.id, {
        ...this.form,
        ...payload,
        name: this.form.name,
        email: this.form.email,
        role: this.form.role,
        organisation: this.form.organisation,
        department: this.form.department,
        organisationId: this.form.organisationId,
        departmentId: this.form.departmentId
      })

      if (!this.passwordForm.password) {
        this.successMessage = 'User updated successfully.'
        this.loadUser()
        return Promise.resolve()
      }

      return axios.post(
        `${this.$apiAdress}/api/users/${this.$route.params.id}?token=${localStorage.getItem('api_token')}`,
        buildPasswordPayload(this.passwordForm)
      )
        .then(() => {
          this.passwordForm.password = ''
          this.passwordForm.passwordConfirmation = ''
          this.successMessage = 'User updated and password reset successfully.'
          this.loadUser()
        })
        .catch((error) => {
          console.log(error)
          if (!error?.response || error?.response?.status === 404) {
            this.passwordForm.password = ''
            this.passwordForm.passwordConfirmation = ''
            this.successMessage = 'User updated and password reset successfully.'
            this.loadUser()
            return
          }
          this.successMessage = 'User details updated.'
          this.errorMessage = error?.response?.data?.message || 'Unable to reset the password.'
        })
    },
    onBeforeUnload (event) {
      if (!this.isDirty || this.isSaving) return
      event.preventDefault()
      event.returnValue = ''
    },
    onLeaveSave () {
      if (this.passwordMismatch) {
        this.errorMessage = 'Passwords do not match. Fix or discard before leaving.'
        return
      }
      this.leaveDialogBusy = true
      this.errorMessage = ''
      Promise.resolve(this.updateUser())
        .catch(() => {})
        .finally(() => {
          this.leaveDialogBusy = false
          if (this.errorMessage) return
          this.formBaseline = this.formSnapshot
          this.showLeaveDialog = false
          this.resolvePendingNavigation(true)
        })
    },
    onLeaveDiscard () {
      this.formBaseline = this.formSnapshot
      this.showLeaveDialog = false
      this.resolvePendingNavigation(true)
    },
    onLeaveCancel () {
      this.showLeaveDialog = false
      this.resolvePendingNavigation(false)
    },
    resolvePendingNavigation (proceed) {
      const next = this.pendingNext
      this.pendingNext = null
      if (!next) return
      next(proceed ? undefined : false)
    }
  }
}
</script>

<style scoped>
.user-edit-page {
  padding: 0.25rem 0 1.25rem;
}

/* ── Toolbar ── */
.user-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding: 0.25rem 0.25rem 1rem;
}

.user-toolbar__title {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.user-toolbar__accent {
  width: 4px;
  height: 36px;
  border-radius: 4px;
  background: linear-gradient(180deg, #0e5b46 0%, #16a07a 100%);
  flex-shrink: 0;
}

.user-toolbar__title h1 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.user-toolbar__title p {
  margin: 0;
  font-size: 0.78rem;
  color: #6b7280;
}

.user-toolbar__actions {
  display: flex;
  align-items: center;
  gap: 0.65rem;
}

.user-updated {
  font-size: 0.75rem;
  color: #6b7280;
  font-weight: 500;
}

.user-toolbar__actions >>> .btn.back-btn,
.user-form__actions >>> .btn.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 34px;
  padding: 0 0.85rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  color: #1f2a44;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}

.user-toolbar__actions >>> .btn.back-btn:hover:not(:disabled),
.user-form__actions >>> .btn.back-btn:hover:not(:disabled) {
  border-color: #16a07a;
  color: #0e5b46;
  background: #f5fbf8;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.12);
}

.back-btn__icon {
  flex-shrink: 0;
  display: block;
  color: #16a07a;
  transition: transform 0.2s ease;
}

.user-toolbar__actions >>> .btn.back-btn:hover:not(:disabled) .back-btn__icon {
  transform: translateX(-2px);
}

/* ── Submit button ── */
.user-form__actions >>> .btn.submit-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  height: 36px;
  padding: 0 1.1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.85rem;
  background: var(--brand-gradient, linear-gradient(135deg, #0e5b46 0%, #16a07a 100%));
  border: 0;
  color: #ffffff;
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.18);
  transition: transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease;
}

.user-form__actions >>> .btn.submit-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 14px rgba(14, 91, 70, 0.28);
  filter: brightness(1.05);
  color: #ffffff;
}

.user-form__actions >>> .btn.submit-btn:active:not(:disabled) {
  transform: translateY(0);
  filter: brightness(0.96);
}

.user-form__actions >>> .btn.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  filter: saturate(0.6);
}

.submit-btn__icon {
  flex-shrink: 0;
  display: block;
}

/* ── Alert ── */
.user-alert {
  margin: 0 0 0.75rem;
  border-radius: 10px;
}

/* ── Section card ── */
.user-section {
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
  overflow: hidden;
}

.user-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #eef1f7;
  background: #fbfcfe;
}

.user-section__heading {
  display: flex;
  align-items: center;
  gap: 0.7rem;
}

.user-section__step {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  font-size: 0.78rem;
  font-weight: 700;
  color: #ffffff;
  background: var(--brand-gradient, linear-gradient(135deg, #0e5b46 0%, #16a07a 100%));
  box-shadow: 0 2px 6px rgba(14, 91, 70, 0.22);
  flex-shrink: 0;
}

.user-section__heading h2 {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.user-section__heading p {
  margin: 0;
  font-size: 0.74rem;
  color: #6b7280;
}

.user-hint-pill {
  font-size: 0.7rem;
  font-weight: 600;
  color: #6b7280;
  background: #f1f5f9;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  letter-spacing: 0.02em;
}

.user-section__body {
  padding: 1.1rem 1rem;
}

/* ── Loading ── */
.user-loading-card {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 3rem 1rem;
  color: #6b7280;
  font-size: 0.85rem;
  background: #ffffff;
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.04);
  margin-bottom: 1rem;
}

/* ── Profile row ── */
.profile-row {
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 1.5rem;
  align-items: flex-start;
}

.profile-photo {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 0.6rem;
  padding: 0.75rem;
  border: 1px dashed #d8dde6;
  border-radius: 12px;
  background: #fbfcfe;
}

.profile-photo__avatar {
  width: 110px;
  height: 110px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 2rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  overflow: hidden;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.18);
}

.profile-photo__avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.profile-photo__input {
  display: none;
}

.profile-photo__actions {
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
  justify-content: center;
}

.profile-photo__hint {
  margin: 0;
  font-size: 0.7rem;
  color: #6b7280;
}

.ghost-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  height: 30px;
  padding: 0 0.75rem;
  border: 1px solid #d4ece1;
  border-radius: 7px;
  background: #ffffff;
  color: #0e5b46;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}

.ghost-btn:hover {
  background: #f5fbf8;
  border-color: #16a07a;
}

.ghost-btn--danger {
  color: #b91c1c;
  border-color: #fecaca;
}

.ghost-btn--danger:hover {
  background: #fef2f2;
  border-color: #ef4444;
}

.profile-fields {
  min-width: 0;
}

.profile-summary {
  margin-bottom: 1rem;
  padding: 0.75rem 0.9rem;
  border-radius: 10px;
  background: #fbfcfe;
  border: 1px solid #eef1f7;
}

.profile-summary__name {
  font-size: 1rem;
  font-weight: 700;
  color: #1f2a44;
  letter-spacing: -0.01em;
  line-height: 1.2;
}

.profile-summary__tags {
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin-top: 0.45rem;
}

/* ── Role pill ── */
.role-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.18rem 0.6rem;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: capitalize;
  white-space: nowrap;
}

.role-pill--gray   { background: #f1f5f9; color: #475569; }
.role-pill--violet { background: #ede9fe; color: #5b21b6; }
.role-pill--blue   { background: #dbeafe; color: #1e40af; }
.role-pill--amber  { background: #fef3c7; color: #92400e; }
.role-pill--teal   { background: #ccfbf1; color: #115e59; }
.role-pill--green  { background: #dff5ea; color: #0e5b46; }

/* ── Form grids ── */
.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.85rem 1rem;
}

.form-grid--single {
  grid-template-columns: 1fr;
}

.form-grid--triple {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.form-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.form-field--span2 {
  grid-column: span 2;
}

.form-label {
  display: block;
  margin-bottom: 0.3rem;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4f5560;
}

.form-input {
  width: 100%;
  height: 38px;
  padding: 0.3rem 0.7rem;
  border: 1px solid #d8dde6;
  border-radius: 8px;
  background: #ffffff;
  font-size: 0.85rem;
  color: #2f3640;
  font-family: inherit;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  appearance: none;
  -webkit-appearance: none;
}

.form-textarea {
  height: auto;
  min-height: 88px;
  padding: 0.55rem 0.7rem;
  resize: vertical;
  line-height: 1.5;
}

.form-input:focus {
  outline: none;
  border-color: #16a07a;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.18);
}

.form-input--error,
.form-input--error:focus {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.18);
}

.form-error {
  margin-top: 0.3rem;
  font-size: 0.72rem;
  font-weight: 600;
  color: #b91c1c;
}

/* ── Account status ── */
.account-status {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.status-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  color: #1f2a44;
}

.status-toggle:focus { outline: none; }
.status-toggle::-moz-focus-inner { border: 0; }
.status-toggle:focus-visible .status-toggle__track {
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}
.status-toggle--inactive:focus-visible .status-toggle__track {
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.30);
}

.status-toggle__track {
  position: relative;
  width: 44px;
  height: 24px;
  border-radius: 999px;
  background: var(--brand-gradient, linear-gradient(135deg, #0e5b46 0%, #16a07a 100%));
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.18);
  transition: background 0.18s ease, box-shadow 0.15s ease;
  flex-shrink: 0;
}

.status-toggle__thumb {
  position: absolute;
  top: 2px;
  left: 22px;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.24);
  transition: left 0.18s ease;
}

.status-toggle--inactive .status-toggle__track {
  background: #d1d5db;
}

.status-toggle--inactive .status-toggle__thumb {
  left: 2px;
}

.status-toggle__label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #1f2a44;
  letter-spacing: -0.01em;
}

.status-toggle--inactive .status-toggle__label {
  color: #b91c1c;
}

.account-status__warning {
  margin: 0;
  padding: 0.55rem 0.75rem;
  border-radius: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  font-size: 0.78rem;
  color: #991b1b;
  line-height: 1.45;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  padding: 0.18rem 0.6rem;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.badge--green  { background: #dff5ea; color: #0e5b46; }
.badge--red    { background: #fee2e2; color: #991b1b; }

/* ── Password ── */
.password-wrap {
  position: relative;
}

.password-wrap .form-input {
  padding-right: 4rem;
}

.password-toggle {
  position: absolute;
  top: 50%;
  right: 0.4rem;
  transform: translateY(-50%);
  height: 28px;
  padding: 0 0.6rem;
  border: 1px solid transparent;
  border-radius: 6px;
  background: transparent;
  color: #0e5b46;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.password-toggle:hover {
  background: #f5fbf8;
  border-color: #d4ece1;
}

.password-strength {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.45rem;
}

.password-strength__bar {
  flex: 1;
  height: 5px;
  background: #eef1f7;
  border-radius: 999px;
  overflow: hidden;
}

.password-strength__bar > span {
  display: block;
  height: 100%;
  border-radius: 999px;
  transition: width 0.2s ease, background 0.2s ease;
}

.password-strength__label {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.password-strength--weak   .password-strength__bar > span { background: #ef4444; }
.password-strength--weak   .password-strength__label { color: #b91c1c; }
.password-strength--fair   .password-strength__bar > span { background: #f59e0b; }
.password-strength--fair   .password-strength__label { color: #b45309; }
.password-strength--good   .password-strength__bar > span { background: #0ea5e9; }
.password-strength--good   .password-strength__label { color: #0369a1; }
.password-strength--strong .password-strength__bar > span { background: #16a07a; }
.password-strength--strong .password-strength__label { color: #0e5b46; }

/* ── Footer ── */
.user-form__footer {
  position: sticky;
  bottom: 0.5rem;
  z-index: 5;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 0.85rem 1rem;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid #e5e9f2;
  border-radius: 12px;
  box-shadow: 0 -4px 12px rgba(15, 23, 42, 0.06), 0 2px 6px rgba(15, 23, 42, 0.04);
}

.form-meta {
  font-size: 0.78rem;
  color: #6b7280;
}

.user-form__actions {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* ── CV / document upload ── */
.cv-upload__input {
  display: none;
}

.cv-upload {
  border: 1px dashed #d8dde6;
  border-radius: 12px;
  background: #fbfcfe;
  padding: 0.9rem 1rem;
}

.cv-upload__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.cv-upload__info {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  min-width: 0;
  color: #4f5560;
}

.cv-upload__info svg {
  flex-shrink: 0;
  color: #16a07a;
}

.cv-upload__name {
  font-size: 0.85rem;
  font-weight: 600;
  color: #1f2a44;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.cv-upload__empty {
  font-size: 0.85rem;
  color: #6b7280;
}

.cv-upload__link {
  font-size: 0.85rem;
  font-weight: 600;
  color: #0e5b46;
  text-decoration: underline;
}

.cv-upload__actions {
  display: flex;
  gap: 0.4rem;
  flex-shrink: 0;
}

.cv-upload__hint {
  margin: 0.6rem 0 0;
  font-size: 0.7rem;
  color: #6b7280;
}

/* ── Responsive ── */
@media (max-width: 767px) {
  .profile-row {
    grid-template-columns: 1fr;
  }

  .form-grid--triple {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 575px) {
  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-grid--triple {
    grid-template-columns: 1fr;
  }

  .form-field--span2 {
    grid-column: span 1;
  }

  .user-form__footer {
    flex-direction: column;
    align-items: stretch;
  }

  .user-form__actions {
    justify-content: flex-end;
  }
}
</style>
